import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Wrench, ListChecks, CheckCircle, Plus, Trash2, RotateCcw, FileDown, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CommuneRetourTab = ({ retourData, missionsSummary, token, onRefresh }) => {
    const [newRetourItem, setNewRetourItem] = useState({ category: 'Autre', label: '' });
    const headers = { Authorization: `Bearer ${token}` };

    const toggleChecklistItem = async (item) => {
        try {
            await axios.put(`${API}/retour-normalite/checklist/${item.id}`, { completed: !item.completed }, { headers });
            onRefresh();
        } catch { toast.error('Erreur'); }
    };
    const updateChecklistComment = async (itemId, comment) => {
        try {
            await axios.put(`${API}/retour-normalite/checklist/${itemId}`, { comment }, { headers });
        } catch { toast.error('Erreur sauvegarde commentaire'); }
    };
    const addChecklistItem = async () => {
        if (!newRetourItem.label.trim()) { toast.error('Le libelle est requis'); return; }
        try {
            await axios.post(`${API}/retour-normalite/checklist`, newRetourItem, { headers });
            setNewRetourItem({ category: 'Autre', label: '' });
            toast.success('Action ajoutee');
            onRefresh();
        } catch { toast.error('Erreur'); }
    };
    const deleteChecklistItem = async (itemId) => {
        try {
            await axios.delete(`${API}/retour-normalite/checklist/${itemId}`, { headers });
            onRefresh();
        } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    };
    const resetChecklist = async () => {
        if (!window.confirm('Reinitialiser toute la checklist ? Toutes les cases seront decochees.')) return;
        try {
            await axios.post(`${API}/retour-normalite/checklist/reset`, {}, { headers });
            toast.success('Checklist reinitialisee');
            onRefresh();
        } catch { toast.error('Erreur'); }
    };
    const exportRetourPdf = () => {
        window.open(`${API}/retour-normalite/export/pdf?token=${token}`, '_blank');
    };

    return (
        <div className="space-y-4">
            {/* 1. Missions operationnelles (Chef de Dispositif) - EN HAUT */}
            <Card className="content-card">
                <CardHeader className="card-header flex flex-row items-center justify-between">
                    <CardTitle className="card-title flex items-center gap-2"><Wrench className="w-5 h-5 text-purple-400" /> Missions opérationnelles (vue lecture)</CardTitle>
                    <a href="/missions" className="text-xs text-blue-400 hover:text-blue-300 inline-flex items-center gap-1">
                        <ExternalLink className="w-3.5 h-3.5" /> Voir le détail
                    </a>
                </CardHeader>
                <CardContent className="p-4">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                        <div className="rounded-lg p-3 bg-blue-500/10 border border-blue-500/20">
                            <p className="text-2xl font-bold text-blue-400">{missionsSummary.active_missions}</p>
                            <p className="text-[11px] text-zinc-400 uppercase tracking-wider">Missions actives</p>
                        </div>
                        <div className="rounded-lg p-3 bg-emerald-500/10 border border-emerald-500/20">
                            <p className="text-2xl font-bold text-emerald-400">{missionsSummary.closed_missions}</p>
                            <p className="text-[11px] text-zinc-400 uppercase tracking-wider">Cloturees</p>
                        </div>
                        <div className="rounded-lg p-3 bg-purple-500/10 border border-purple-500/20">
                            <p className="text-2xl font-bold text-purple-400">{missionsSummary.total_sub_missions}</p>
                            <p className="text-[11px] text-zinc-400 uppercase tracking-wider">Sous-missions</p>
                        </div>
                        <div className="rounded-lg p-3 bg-amber-500/10 border border-amber-500/20">
                            <p className="text-2xl font-bold text-amber-400">{missionsSummary.closed_sub_missions}/{missionsSummary.total_sub_missions || 0}</p>
                            <p className="text-[11px] text-zinc-400 uppercase tracking-wider">Sous-missions OK</p>
                        </div>
                    </div>
                    {missionsSummary.missions.length === 0 ? (
                        <p className="text-center py-4 text-zinc-500 text-sm">Aucune mission de retour à la normalité</p>
                    ) : (
                        <div className="space-y-2">
                            {missionsSummary.missions.slice(0, 10).map(m => {
                                const closedSub = m.sub_missions ? m.sub_missions.filter(s => s.status === 'closed').length : 0;
                                const totalSub = m.sub_missions ? m.sub_missions.length : 0;
                                return (
                                    <div key={m.id} className={`p-2.5 rounded-lg border ${m.status === 'closed' ? 'bg-zinc-800/40 border-zinc-700 opacity-60' : 'bg-blue-500/5 border-blue-500/20'}`}>
                                        <div className="flex items-center justify-between gap-2">
                                            <div className="min-w-0 flex-1">
                                                <p className="text-sm text-white truncate">{m.title}</p>
                                                <p className="text-[11px] text-zinc-500">
                                                    {m.chef_secteur_name ? `Chef secteur: ${m.chef_secteur_name}` : 'Sans chef secteur'} - Sous-missions: {closedSub}/{totalSub}
                                                </p>
                                            </div>
                                            <Badge variant="outline" className={`text-[10px] ${m.status === 'closed' ? 'border-emerald-500/30 text-emerald-400' : 'border-blue-500/30 text-blue-400'}`}>
                                                {m.status === 'closed' ? 'Cloturee' : 'Active'}
                                            </Badge>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* 2. Progression + actions */}
            <Card className="content-card">
                <CardContent className="p-5">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                        <div className="flex-1 min-w-[280px]">
                            <div className="flex items-baseline gap-3 mb-2">
                                <h3 className="text-xl font-bold text-white">Progression du retour à la normalité</h3>
                                <span className="text-sm text-zinc-400 font-mono">{retourData.completed}/{retourData.total} actions ({retourData.progress}%)</span>
                            </div>
                            <div className="w-full h-3 bg-zinc-800 rounded-full overflow-hidden">
                                <div className={`h-full transition-all ${retourData.progress >= 100 ? 'bg-emerald-500' : retourData.progress > 60 ? 'bg-blue-500' : retourData.progress > 30 ? 'bg-amber-500' : 'bg-red-500'}`}
                                    style={{ width: `${Math.min(100, retourData.progress)}%` }}
                                    data-testid="retour-progress-bar"></div>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <Button size="sm" variant="outline" onClick={resetChecklist} className="text-amber-400 border-amber-500/30 hover:bg-amber-500/10" data-testid="retour-reset-btn">
                                <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reinitialiser
                            </Button>
                            <Button size="sm" className="bg-blue-600 hover:bg-blue-500" onClick={exportRetourPdf} data-testid="retour-export-pdf-btn">
                                <FileDown className="w-3.5 h-3.5 mr-1.5" /> Exporter PDF
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* 3. Ajout d'une action commune */}
            <Card className="content-card">
                <CardContent className="p-4">
                    <div className="flex items-end gap-2 flex-wrap sm:flex-nowrap">
                        <div className="w-full sm:w-40">
                            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Categorie</Label>
                            <Input value={newRetourItem.category} onChange={e => setNewRetourItem({ ...newRetourItem, category: e.target.value })} className="tactical-input h-8" placeholder="Autre" />
                        </div>
                        <div className="flex-1 min-w-[200px]">
                            <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Nouvelle action commune</Label>
                            <Input value={newRetourItem.label} onChange={e => setNewRetourItem({ ...newRetourItem, label: e.target.value })} onKeyDown={e => e.key === 'Enter' && addChecklistItem()} className="tactical-input h-8" placeholder="Ex: Verifier la voirie principale, planifier reunion de bilan..." data-testid="retour-new-label" />
                        </div>
                        <Button size="sm" className="bg-blue-600 hover:bg-blue-500 h-8" onClick={addChecklistItem} data-testid="retour-add-btn">
                            <Plus className="w-4 h-4 mr-1" /> Ajouter
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* 4. Checklist groupee par categorie */}
            <Card className="content-card">
                <CardHeader className="card-header"><CardTitle className="card-title flex items-center gap-2"><ListChecks className="w-5 h-5 text-blue-400" /> Checklist commune</CardTitle></CardHeader>
                <CardContent className="p-4 space-y-3">
                    {Object.entries(retourData.items.reduce((acc, it) => {
                        const cat = it.category || 'Autre';
                        if (!acc[cat]) acc[cat] = [];
                        acc[cat].push(it);
                        return acc;
                    }, {})).map(([category, items]) => (
                        <div key={category} className="rounded-lg border border-zinc-800 overflow-hidden">
                            <div className="px-3 py-2 bg-zinc-800/50 text-xs font-semibold uppercase tracking-wider text-zinc-400">{category}</div>
                            <div className="divide-y divide-zinc-800">
                                {items.map(it => (
                                    <div key={it.id} className={`p-3 flex items-start gap-3 ${it.completed ? 'bg-emerald-500/5' : ''}`} data-testid={`retour-item-${it.id}`}>
                                        <button
                                            onClick={() => toggleChecklistItem(it)}
                                            className={`flex-shrink-0 w-5 h-5 mt-0.5 rounded border-2 flex items-center justify-center transition ${it.completed ? 'bg-emerald-500 border-emerald-500' : 'border-zinc-600 hover:border-zinc-400'}`}
                                            data-testid={`retour-check-${it.id}`}
                                            title={it.completed ? 'Décocher' : 'Marquer comme fait'}
                                        >
                                            {it.completed && <CheckCircle className="w-3.5 h-3.5 text-white" />}
                                        </button>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className={`text-sm ${it.completed ? 'line-through text-zinc-500' : 'text-white'}`}>{it.label}</span>
                                                {it.module_link && (
                                                    <a href={it.module_link} className="text-[10px] text-blue-400 hover:text-blue-300 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-blue-500/10">
                                                        <ExternalLink className="w-2.5 h-2.5" /> {it.module_label}
                                                    </a>
                                                )}
                                                {!it.is_default && <Badge variant="outline" className="text-[9px] border-zinc-700 text-zinc-500">custom</Badge>}
                                            </div>
                                            {it.completed && (it.completed_by || it.completed_at) && (
                                                <p className="text-[11px] text-emerald-400/80 mt-1">
                                                    ✓ Fait{it.completed_by ? ` par ${it.completed_by}` : ''}{it.completed_at ? ` le ${new Date(it.completed_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}` : ''}
                                                </p>
                                            )}
                                            <Input
                                                defaultValue={it.comment || ''}
                                                placeholder="Commentaire (optionnel)..."
                                                onBlur={(e) => { if ((e.target.value || '') !== (it.comment || '')) updateChecklistComment(it.id, e.target.value); }}
                                                className="tactical-input mt-1.5 h-7 text-xs"
                                                data-testid={`retour-comment-${it.id}`}
                                            />
                                        </div>
                                        {!it.is_default && (
                                            <Button size="sm" variant="ghost" className="text-red-500 h-7 w-7 p-0 flex-shrink-0" onClick={() => deleteChecklistItem(it.id)}>
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </Button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </CardContent>
            </Card>
        </div>
    );
};

export default CommuneRetourTab;
